import { useEffect, useState } from 'react'
import useSWR from 'swr'
import styled from 'styled-components'
import { getAddress, isAddress } from 'viem'
import { getCadinuProfileAddress } from 'utils/addressHelpers'
import { useAccount, useContractWrite, useWaitForTransaction } from 'wagmi'
import { Avatar, api } from './api'
import { ActionBtn, Icon, LevelBadge, Note, SectionTitle, Surface } from './Identity'
import { Btn, GLOW, Muted } from './ui'

const ERC721_SEND = [
  { name: 'safeTransferFrom', type: 'function', stateMutability: 'nonpayable', inputs: [{ name: 'from', type: 'address' }, { name: 'to', type: 'address' }, { name: 'tokenId', type: 'uint256' }], outputs: [] },
] as const

const Field = styled.input`
  min-height: 46px;
  border-radius: 12px;
  padding: 0 12px;
  font-size: 15px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(0, 0, 0, 0.25);
  color: inherit;
  width: 100%;
  &:focus-visible {
    outline: 2px solid ${GLOW};
    border-color: transparent;
  }
`

/** Sending an NFT (the old profile's "Transfer" action). A held NFT only; the one locked in the on-chain profile must be unlocked (paused) first. */
function SendNft({ nft, from, onDone }: { nft: Avatar; from: `0x${string}`; onDone: () => void }) {
  const [to, setTo] = useState('')
  const [ok, setOk] = useState(false)
  const bad = !isAddress(to) ? 'Not an address' : to.toLowerCase() === from.toLowerCase() ? 'That is your own wallet' : to.toLowerCase() === getCadinuProfileAddress().toLowerCase() ? 'Use "Register on-chain" to lock an NFT' : null
  const { write, data, isLoading } = useContractWrite({ address: nft.collection as `0x${string}`, abi: ERC721_SEND, functionName: 'safeTransferFrom' } as any) as any
  const { isLoading: mining, isSuccess } = useWaitForTransaction({ hash: data?.hash as `0x${string}` | undefined, onSuccess: onDone })
  return (
    <div style={{ display: 'grid', gap: 10 }} data-testid="send-form">
      <strong style={{ fontSize: 15 }}>Send {nft.character} #{nft.token_id}</strong>
      <Field aria-label="Recipient" placeholder="Recipient 0x…" value={to} onChange={(e) => setTo(e.target.value.trim())} />
      {to && bad && <Muted role="alert">{bad}</Muted>}
      <label htmlFor={`send-ok-${nft.collection}-${nft.token_id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
        <input id={`send-ok-${nft.collection}-${nft.token_id}`} type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} />
        I understand a transfer cannot be undone.
      </label>
      <Btn type="button" disabled={Boolean(bad) || !ok || isLoading || mining} onClick={() => write({ args: [from, getAddress(to), BigInt(nft.token_id)] })}>
        {isLoading ? 'Confirm in wallet…' : mining ? 'Sending…' : isSuccess ? 'Sent' : 'Send NFT'}
      </Btn>
    </div>
  )
}

const Grid = styled.ul`
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`
const Tile = styled.li<{ $lvl?: number | null }>`
  position: relative;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 16px 30px -18px rgba(0, 0, 0, 0.8);
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 180ms;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 36px -16px rgba(0, 0, 0, 0.8), 0 0 0 1px ${({ $lvl }) => ($lvl === 2 ? 'rgba(179,139,255,0.5)' : 'rgba(127,212,232,0.45)')};
  }
  .art {
    aspect-ratio: 1;
    width: 100%;
    display: block;
    border-radius: 19px 19px 0 0;
    object-fit: cover;
    background: radial-gradient(100% 100% at 50% 0%, rgba(154, 123, 255, 0.3), #0d1530);
  }
  .body {
    padding: 10px 10px 12px;
    display: grid;
    gap: 6px;
  }
  .name {
    font-size: 15px;
    font-weight: 700;
    text-transform: capitalize;
    color: ${({ theme }) => theme.colors.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
  }
  .menu-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 36px;
    height: 36px;
    border-radius: 12px;
    border: 0;
    display: grid;
    place-items: center;
    cursor: pointer;
    color: #fff;
    background: rgba(8, 12, 30, 0.6);
    backdrop-filter: blur(6px);
  }
  .menu-btn:focus-visible {
    outline: 2px solid ${GLOW};
  }
  .locked {
    position: absolute;
    top: 12px;
    left: 8px;
    max-width: calc(100% - 60px);
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 28px;
    padding: 0 9px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
    color: #0b1220;
    background: #8ff0c4;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
const Menu = styled.div`
  position: absolute;
  top: 48px;
  right: 8px;
  z-index: 3;
  width: max-content;
  max-width: calc(100vw - 48px);
  padding: 6px;
  border-radius: 14px;
  background: #0d1530;
  border: 1px solid rgba(127, 212, 232, 0.25);
  box-shadow: 0 18px 40px -12px rgba(0, 0, 0, 0.8);
  display: grid;
  gap: 2px;
  a,
  button {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 42px;
    padding: 0 12px;
    border-radius: 10px;
    border: 0;
    background: none;
    font: inherit;
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;
    text-decoration: none;
    text-align: left;
  }
  a:hover,
  button:hover {
    background: rgba(127, 212, 232, 0.12);
  }
`
const SendSheet = styled.div`
  margin-top: 12px;
  padding: 14px;
  border-radius: 16px;
  background: rgba(127, 212, 232, 0.06);
  border: 1px solid rgba(127, 212, 232, 0.2);
`
const Empty = styled.div`
  margin-top: 14px;
  padding: 24px 16px;
  border-radius: 18px;
  text-align: center;
  display: grid;
  gap: 10px;
  justify-items: center;
  background: rgba(255, 255, 255, 0.025);
  border: 1px dashed rgba(127, 212, 232, 0.25);
  color: ${({ theme }) => theme.colors.textSubtle};
`

export function NftsCard({ wallet, mine, onCount, onSetAvatar, avatarKey, onBuy }: { wallet: string; mine?: boolean; onCount?: (n: number) => void; onSetAvatar?: (n: Avatar) => void; avatarKey?: string | null; onBuy?: () => void }) {
  const { address } = useAccount()
  const { data, mutate } = useSWR<{ nfts: Avatar[]; state?: string }>(`nfts:${wallet}`, () => api(`profiles/${wallet}/nfts`), { revalidateOnFocus: false })
  const [menu, setMenu] = useState<string | null>(null)
  const [sending, setSending] = useState<string | null>(null)
  const [all, setAll] = useState(false)
  const SHOW = 8
  const canSend = Boolean(mine && address && address.toLowerCase() === wallet.toLowerCase())
  const n = data?.nfts.length ?? 0
  useEffect(() => {
    if (data) onCount?.(data.nfts.length)
  }, [data, onCount])
  useEffect(() => {
    if (!menu) return undefined
    const close = () => setMenu(null)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [menu])
  const sendingNft = data?.nfts.find((x) => `${x.collection}-${x.token_id}` === sending)
  return (
    <Surface data-testid="nfts-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <SectionTitle as="h2">
          CADINU NFTs
          {n > 0 && <strong style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>{n}</strong>}
        </SectionTitle>
        {mine && onBuy && n > 0 && (
          <ActionBtn type="button" onClick={onBuy} data-testid="nfts-buy" style={{ minHeight: 40, padding: '0 14px' }}>
            <Icon name="image" size={16} />
            Buy
          </ActionBtn>
        )}
      </div>
      {!data && <Note style={{ marginTop: 10 }}>Reading BNB Chain…</Note>}
      {data && !n && (
        <Empty>
          <Icon name="image" size={28} />
          <span>No CADINU Identity Arts NFT in this wallet.</span>
          {mine && (
            <ActionBtn type="button" onClick={onBuy} $primary data-testid="nfts-buy">
              Buy a CADINU NFT
            </ActionBtn>
          )}
        </Empty>
      )}
      {n > 0 && (
        <Grid>
          {data!.nfts.slice(0, all ? undefined : SHOW).map((x) => {
            const k = `${x.collection}-${x.token_id}`
            const label = `${x.character ?? 'NFT'} #${x.token_id}`
            return (
              <Tile key={k} $lvl={x.level} data-testid="nft-tile">
                {x.image ? <img className="art" src={x.image} alt={label} loading="lazy" /> : <div className="art" />}
                {x.source === 'profile' && (
                  <span className="locked">
                    <Icon name="lock" size={12} />
                    Locked<span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}> in profile</span>
                  </span>
                )}
                <button
                  type="button"
                  className="menu-btn"
                  aria-label={`Options for ${label}`}
                  aria-haspopup="menu"
                  aria-expanded={menu === k}
                  onClick={(e) => {
                    e.stopPropagation()
                    setMenu(menu === k ? null : k)
                  }}
                >
                  <Icon name="more" size={18} />
                </button>
                {menu === k && (
                  <Menu role="menu" onClick={(e) => e.stopPropagation()}>
                    {canSend && x.source === 'wallet' && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setSending(k)
                          setMenu(null)
                        }}
                      >
                        <Icon name="send" size={18} />
                        Send
                      </button>
                    )}
                    {canSend && onSetAvatar && avatarKey !== k && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          onSetAvatar(x)
                          setMenu(null)
                        }}
                      >
                        <Icon name="badge" size={18} />
                        Use as avatar
                      </button>
                    )}
                    <a role="menuitem" href={`https://bscscan.com/token/${x.collection}?a=${x.token_id}`} target="_blank" rel="noreferrer">
                      <Icon name="external" size={18} />
                      View on BscScan
                    </a>
                  </Menu>
                )}
                <div className="body">
                  <span className="name">{label}</span>
                  <span className="chips">
                    {x.level && (
                      <LevelBadge $lvl={x.level} style={{ minHeight: 26, fontSize: 12, padding: '0 9px' }}>
                        Level {x.level}
                      </LevelBadge>
                    )}
                    {avatarKey === k && <span style={{ fontSize: 12, color: '#8fe3f5', fontWeight: 700 }}>Avatar</span>}
                  </span>
                </div>
              </Tile>
            )
          })}
        </Grid>
      )}
      {canSend && sendingNft && (
        <SendSheet>
          <SendNft nft={sendingNft} from={address as `0x${string}`} onDone={() => mutate()} />
          <Btn type="button" $ghost style={{ marginTop: 10 }} onClick={() => setSending(null)}>
            Cancel
          </Btn>
        </SendSheet>
      )}
      {n > SHOW && (
        <ActionBtn type="button" style={{ marginTop: 14, width: '100%' }} onClick={() => setAll(!all)}>
          {all ? 'Show fewer' : `Show all ${n}`}
        </ActionBtn>
      )}
    </Surface>
  )
}
